import { AIModule } from '../types';

export const ADVANCED_RL_ALGORITHMS_MODULES: AIModule[] = [
  {
    id: 'advanced-rl-algorithms',
    title: 'Advanced RL Algorithms',
    description: 'Master cutting-edge reinforcement learning algorithms including SAC, TD3, and Rainbow DQN.',
    estimatedTime: '105 minutes',
    duration: '105 minutes',
    difficulty: 'advanced' as const,
    content: `
# Advanced Reinforcement Learning Algorithms

## State-of-the-Art Algorithms

### 1. Soft Actor-Critic (SAC)
Maximum entropy RL algorithm that encourages exploration while learning optimal policies.

\`\`\`python
import torch
import torch.nn as nn
import torch.optim as optim
import numpy as np

class SACAgent:
    def __init__(self, state_dim, action_dim, hidden_dim=256):
        self.state_dim = state_dim
        self.action_dim = action_dim
        
        # Networks
        self.actor = PolicyNetwork(state_dim, action_dim, hidden_dim)
        self.critic1 = QNetwork(state_dim, action_dim, hidden_dim)
        self.critic2 = QNetwork(state_dim, action_dim, hidden_dim)
        self.critic1_target = QNetwork(state_dim, action_dim, hidden_dim)
        self.critic2_target = QNetwork(state_dim, action_dim, hidden_dim)
        
        # Temperature parameter for entropy regularization
        self.log_alpha = torch.tensor(np.log(0.2), requires_grad=True)
        self.alpha = self.log_alpha.exp()
        
        # Optimizers
        self.actor_optimizer = optim.Adam(self.actor.parameters(), lr=3e-4)
        self.critic1_optimizer = optim.Adam(self.critic1.parameters(), lr=3e-4)
        self.critic2_optimizer = optim.Adam(self.critic2.parameters(), lr=3e-4)
        self.alpha_optimizer = optim.Adam([self.log_alpha], lr=3e-4)
        
    def select_action(self, state, evaluate=False):
        state = torch.FloatTensor(state).unsqueeze(0)
        if evaluate:
            action, _ = self.actor.sample(state)
        else:
            action, _ = self.actor.sample(state)
        return action.detach().cpu().numpy()[0]
        
    def update(self, batch):
        state_batch, action_batch, reward_batch, next_state_batch, done_batch = batch
        
        # Update critics
        with torch.no_grad():
            next_action, next_log_prob = self.actor.sample(next_state_batch)
            target_q1 = self.critic1_target(next_state_batch, next_action)
            target_q2 = self.critic2_target(next_state_batch, next_action)
            target_q = torch.min(target_q1, target_q2) - self.alpha * next_log_prob
            target_q = reward_batch + (1 - done_batch) * 0.99 * target_q
            
        current_q1 = self.critic1(state_batch, action_batch)
        current_q2 = self.critic2(state_batch, action_batch)
        
        critic1_loss = nn.MSELoss()(current_q1, target_q)
        critic2_loss = nn.MSELoss()(current_q2, target_q)
        
        # Update actor
        new_action, log_prob = self.actor.sample(state_batch)
        q1_new = self.critic1(state_batch, new_action)
        q2_new = self.critic2(state_batch, new_action)
        q_new = torch.min(q1_new, q2_new)
        
        actor_loss = (self.alpha * log_prob - q_new).mean()
        
        # Update temperature
        alpha_loss = -(self.log_alpha * (log_prob + self.action_dim).detach()).mean()
        
        # Perform updates
        self.critic1_optimizer.zero_grad()
        critic1_loss.backward()
        self.critic1_optimizer.step()
        
        self.critic2_optimizer.zero_grad()
        critic2_loss.backward()
        self.critic2_optimizer.step()
        
        self.actor_optimizer.zero_grad()
        actor_loss.backward()
        self.actor_optimizer.step()
        
        self.alpha_optimizer.zero_grad()
        alpha_loss.backward()
        self.alpha_optimizer.step()
        
        self.alpha = self.log_alpha.exp()

class PolicyNetwork(nn.Module):
    def __init__(self, state_dim, action_dim, hidden_dim):
        super().__init__()
        self.network = nn.Sequential(
            nn.Linear(state_dim, hidden_dim),
            nn.ReLU(),
            nn.Linear(hidden_dim, hidden_dim),
            nn.ReLU()
        )
        self.mean_head = nn.Linear(hidden_dim, action_dim)
        self.log_std_head = nn.Linear(hidden_dim, action_dim)
        
    def sample(self, state):
        features = self.network(state)
        mean = self.mean_head(features)
        log_std = self.log_std_head(features).clamp(-20, 2)
        std = log_std.exp()
        
        normal = torch.distributions.Normal(mean, std)
        x_t = normal.rsample()
        action = torch.tanh(x_t)
        
        log_prob = normal.log_prob(x_t)
        log_prob -= torch.log(1 - action.pow(2) + 1e-6)
        log_prob = log_prob.sum(1, keepdim=True)
        
        return action, log_prob
\`\`\`

### 2. Twin Delayed Deep Deterministic Policy Gradient (TD3)

\`\`\`python
class TD3Agent:
    def __init__(self, state_dim, action_dim, max_action):
        self.actor = Actor(state_dim, action_dim, max_action)
        self.actor_target = Actor(state_dim, action_dim, max_action)
        self.critic = Critic(state_dim, action_dim)
        self.critic_target = Critic(state_dim, action_dim)
        
        self.actor_optimizer = optim.Adam(self.actor.parameters())
        self.critic_optimizer = optim.Adam(self.critic.parameters())
        
        self.max_action = max_action
        self.policy_noise = 0.2
        self.noise_clip = 0.5
        self.policy_freq = 2
        self.total_it = 0
        
    def select_action(self, state):
        state = torch.FloatTensor(state.reshape(1, -1))
        return self.actor(state).cpu().data.numpy().flatten()
        
    def train(self, replay_buffer, batch_size=256):
        self.total_it += 1
        
        # Sample replay buffer
        state, action, next_state, reward, not_done = replay_buffer.sample(batch_size)
        
        with torch.no_grad():
            # Select action according to policy and add clipped noise
            noise = (torch.randn_like(action) * self.policy_noise).clamp(
                -self.noise_clip, self.noise_clip)
            next_action = (self.actor_target(next_state) + noise).clamp(
                -self.max_action, self.max_action)
                
            # Compute the target Q value
            target_Q1, target_Q2 = self.critic_target(next_state, next_action)
            target_Q = torch.min(target_Q1, target_Q2)
            target_Q = reward + not_done * 0.99 * target_Q
            
        # Get current Q estimates
        current_Q1, current_Q2 = self.critic(state, action)
        
        # Compute critic loss
        critic_loss = nn.MSELoss()(current_Q1, target_Q) + nn.MSELoss()(current_Q2, target_Q)
        
        # Optimize the critic
        self.critic_optimizer.zero_grad()
        critic_loss.backward()
        self.critic_optimizer.step()
        
        # Delayed policy updates
        if self.total_it % self.policy_freq == 0:
            # Compute actor loss
            actor_loss = -self.critic.Q1(state, self.actor(state)).mean()
            
            # Optimize the actor
            self.actor_optimizer.zero_grad()
            actor_loss.backward()
            self.actor_optimizer.step()
            
            # Update the frozen target models
            for param, target_param in zip(self.critic.parameters(), self.critic_target.parameters()):
                target_param.data.copy_(0.005 * param.data + (1 - 0.005) * target_param.data)
                
            for param, target_param in zip(self.actor.parameters(), self.actor_target.parameters()):
                target_param.data.copy_(0.005 * param.data + (1 - 0.005) * target_param.data)
\`\`\`

### 3. Rainbow DQN
Combines multiple DQN improvements into a single algorithm.

\`\`\`python
class RainbowDQN:
    def __init__(self, state_dim, action_dim, atoms=51, v_min=-10, v_max=10):
        self.action_dim = action_dim
        self.atoms = atoms
        self.v_min = v_min
        self.v_max = v_max
        self.delta_z = (v_max - v_min) / (atoms - 1)
        self.supports = torch.linspace(v_min, v_max, atoms)
        
        # Rainbow components
        self.network = RainbowNetwork(state_dim, action_dim, atoms)
        self.target_network = RainbowNetwork(state_dim, action_dim, atoms)
        self.optimizer = optim.Adam(self.network.parameters())
        
        # Prioritized replay
        self.memory = PrioritizedReplayBuffer(100000)
        
        # Noisy networks (built into network)
        # Dueling architecture (built into network)
        # Distributional RL (implemented here)
        # Multi-step learning
        self.n_step = 3
        
    def project_distribution(self, next_dist, rewards, dones, gamma=0.99):
        batch_size = next_dist.size(0)
        delta_z = float(self.delta_z)
        support = self.supports.expand_as(next_dist)
        
        Tz = rewards.unsqueeze(1) + (1 - dones.unsqueeze(1)) * gamma * support
        Tz = Tz.clamp(min=self.v_min, max=self.v_max)
        b = (Tz - self.v_min) / delta_z
        l = b.floor().long()
        u = b.ceil().long()
        
        offset = torch.linspace(0, (batch_size - 1) * self.atoms, batch_size).long()\\
            .unsqueeze(1).expand(batch_size, self.atoms)
            
        proj_dist = torch.zeros(next_dist.size())
        proj_dist.view(-1).index_add_(0, (l + offset).view(-1), (next_dist * (u.float() - b)).view(-1))
        proj_dist.view(-1).index_add_(0, (u + offset).view(-1), (next_dist * (b - l.float())).view(-1))
        
        return proj_dist
\`\`\`

## Advanced Techniques

### Hierarchical RL
- **Option frameworks**: Temporal abstractions
- **Goal-conditioned RL**: Flexible goal specification
- **Curriculum learning**: Progressive difficulty

### Meta-Learning in RL
- **Model-Agnostic Meta-Learning (MAML)**: Fast adaptation
- **Learning to learn**: Transfer across tasks
- **Few-shot RL**: Quick adaptation to new environments

### Offline RL
- **Conservative Q-Learning (CQL)**: Learning from fixed datasets
- **Behavior cloning**: Imitation from demonstrations
- **Distributional corrections**: Handling distribution shift
`,
    learningObjectives: [
      'Implement advanced RL algorithms like SAC and TD3',
      'Understand distributional reinforcement learning',
      'Apply hierarchical and meta-learning approaches',
      'Handle offline and batch reinforcement learning'
    ],
    quiz: {
      questions: [
        {
          id: 'advanced-rl-1',
          question: 'What is the key innovation of Soft Actor-Critic (SAC)?',
          options: [
            'Double Q-learning',
            'Maximum entropy objective',
            'Experience replay',
            'Target networks'
          ],
          correctAnswer: 1,
          explanation: 'SAC uses a maximum entropy objective that encourages exploration while learning optimal policies.'
        },
        {
          id: 'advanced-rl-2',
          question: 'Why does TD3 use twin critics?',
          options: [
            'Faster training',
            'Reduced overestimation bias',
            'Better exploration',
            'Memory efficiency'
          ],
          correctAnswer: 1,
          explanation: 'Twin critics in TD3 help reduce overestimation bias by taking the minimum of two Q-value estimates.'
        },
        {
          id: 'advanced-rl-3',
          question: 'What does Rainbow DQN combine?',
          options: [
            'Only prioritized replay and dueling networks',
            'Six different DQN improvements',
            'Three basic DQN variants',
            'Actor-critic methods'
          ],
          correctAnswer: 1,
          explanation: 'Rainbow DQN combines six improvements: double DQN, prioritized replay, dueling networks, multi-step learning, distributional RL, and noisy networks.'
        },
        {
          id: 'advanced-rl-4',
          question: 'What is the main advantage of hierarchical RL?',
          options: [
            'Faster convergence',
            'Temporal abstraction and structured exploration',
            'Less memory usage',
            'Simpler implementation'
          ],
          correctAnswer: 1,
          explanation: 'Hierarchical RL provides temporal abstraction, allowing agents to plan and act at multiple time scales.'
        },
        {
          id: 'advanced-rl-5',
          question: 'When is offline RL most useful?',
          options: [
            'When online interaction is cheap',
            'When online interaction is expensive or dangerous',
            'For simple environments only',
            'When data is unlimited'
          ],
          correctAnswer: 1,
          explanation: 'Offline RL is most useful when online interaction is expensive, dangerous, or impractical, allowing learning from fixed datasets.'
        }
      ]
    }
  }
];